import _ from 'lodash';
import React from 'react';
import {submit, isSubmitting, formValueSelector} from 'redux-form';

import {
  MODALS,
  fetchImageUrlAndFileName,
  fetchDocumentFileName
} from 'ducks/bookingSets';

import Button from 'components/Button';
import {default as Modal, withModal} from 'components/Modal';
import {humanReadableBoolean} from 'utils/common';

import './styles.css';
import ImageWithFilename from 'components/ImageWithFilename';
import DocumentWithFilename from 'components/DocumentWithFilename';
import {EmptyListPlaceholder} from 'containers/BookingSetDetail/components';

class ConfirmAddDepartureRoomCheckModal extends React.Component<any> {
  componentDidMount() {
    const {
      data: {
        addDepartureRoomCheckForm: {
          imageIdentifiers,
          inventoryIdentifier,
          maintenanceIdentifier
        }
      }
    } = this.props;

    if (!_.isEmpty(imageIdentifiers)) {
      imageIdentifiers.map(imageIdentifier => {
        const extraActions = this.props.storeDataExtraActions(data => ({
          images: {
            ...this.props.data.images,
            [imageIdentifier]: data
          }
        }));

        this.props.fetchImageUrlAndFileName(
          {
            imageIdentifier
          },
          extraActions
        );
      });
    }

    if (!_.isNull(inventoryIdentifier)) {
      const extraActions = this.props.storeDataExtraActions(data => ({
        inventory: data
      }));

      this.props.fetchDocumentFileName(
        {documentIdentifier: inventoryIdentifier},
        extraActions
      );
    }

    if (!_.isNull(maintenanceIdentifier)) {
      const extraActions = this.props.storeDataExtraActions(data => ({
        maintenance: data
      }));

      this.props.fetchDocumentFileName(
        {documentIdentifier: maintenanceIdentifier},
        extraActions
      );
    }
  }

  onSubmit = () => {
    this.props.submit('addDepartureRoomCheckForm');
  };

  render() {
    const {
      data: {
        images,
        inventory,
        maintenance,
        submitting,
        addDepartureRoomCheckForm: {
          includesDamages,
          damagesChargeTotal,
          addDepartureCheckDescription
        },
        chargeForm: {
          oneOffChargeReason,
          description,
          paymentMethod,
          createPayment,
          paymentDate,
          amount
        }
      }
    } = this.props;

    const sections_from_add_form = [
      {
        heading: 'Includes damages',
        value: humanReadableBoolean(includesDamages) || '-'
      },
      {
        heading: 'Damages charge total',
        value: `£${damagesChargeTotal || '-'}`
      },
      {
        heading: 'Description',
        value: `${addDepartureCheckDescription || '-'}`
      }
    ];

    const sections_from_charge_form = [
      {
        heading: 'Charge Item type',
        value: _.get(oneOffChargeReason, 'label', '-')
      },
      {heading: 'Charge description', value: description || '-'},
      {heading: 'Amount', value: `£${amount || '-'}`},
      {
        heading: 'Payment received',
        value: humanReadableBoolean(createPayment) || '-'
      },
      {heading: 'Payment methods', value: _.get(paymentMethod, 'label', '-')},
      {heading: 'Payment date', value: paymentDate || '-'}
    ];

    return (
      <Modal
        show={true}
        onClose={this.props.closeModal}
        type={Modal.EXTRA_SMALL}
        className="ConfirmAddDepartureRoomCheckModal">
        <Modal.Header heading="Confirm departure room check" />
        <Modal.Body>
          {sections_from_add_form.map(section => (
            <div
              key={section.heading}
              className="ConfirmAddDepartureRoomCheckModal__section">
              <div className="ConfirmAddDepartureRoomCheckModal__section__label colab-bold">
                {section.heading}
              </div>
              <div className="ConfirmAddDepartureRoomCheckModal__section__value">
                {section.value}
              </div>
            </div>
          ))}
          <div className="ConfirmAddDepartureRoomCheckModal__section">
            <div className="ConfirmAddDepartureRoomCheckModal__section__label colab-bold">
              Images
            </div>
            {!_.isEmpty(images) &&
              _.map(images, image => (
                <ImageWithFilename
                  key={image.imageUrl}
                  imageUrl={image.imageUrl}
                  filename={image.filename}
                />
              ))}
            {_.isEmpty(images) && <EmptyListPlaceholder />}
          </div>
          <div className="ConfirmAddDepartureRoomCheckModal__section">
            <div className="ConfirmAddDepartureRoomCheckModal__section__label colab-bold">
              Inventory
            </div>
            {!_.isNil(inventory) && (
              <DocumentWithFilename filename={inventory.filename} />
            )}
            {_.isNil(inventory) && <EmptyListPlaceholder />}
          </div>
          <div className="ConfirmAddDepartureRoomCheckModal__section">
            <div className="ConfirmAddDepartureRoomCheckModal__section__label colab-bold">
              Maintenance
            </div>
            {!_.isNil(maintenance) && (
              <DocumentWithFilename filename={maintenance.filename} />
            )}
            {_.isNil(maintenance) && <EmptyListPlaceholder />}
          </div>
          {sections_from_charge_form.map(section => (
            <div
              key={section.heading}
              className="ConfirmAddDepartureRoomCheckModal__section">
              <div className="ConfirmAddDepartureRoomCheckModal__section__label colab-bold">
                {section.heading}
              </div>
              <div className="ConfirmAddDepartureRoomCheckModal__section__value">
                {section.value}
              </div>
            </div>
          ))}
        </Modal.Body>
        <Modal.Footer className="colab-row-space-between">
          <Button className="goBack" white onClick={this.props.closeModal}>
            Go back
          </Button>
          <Button
            className="submit"
            onClick={this.onSubmit}
            blue={!submitting}
            isLoading={submitting}
            disabled={submitting}>
            Yes
          </Button>
        </Modal.Footer>
      </Modal>
    );
  }
}

const chargeFormSelector = formValueSelector(
  'createOneOffChargeInvoiceAndPayment'
);
const addRoomCheckFormSelector = formValueSelector('addDepartureRoomCheckForm');

const mapStateToModalData = (state, data) => ({
  inventory: _.get(data, 'inventory', null),
  maintenance: _.get(data, 'maintenance', null),
  images: _.get(data, 'images', []),
  submitting: isSubmitting('createOneOffChargeInvoiceAndPayment')(state),
  addDepartureRoomCheckForm: {
    includesDamages: addRoomCheckFormSelector(state, 'includesDamages'),
    damagesChargeTotal: addRoomCheckFormSelector(state, 'totalDamageCharge'),
    addDepartureCheckDescription: addRoomCheckFormSelector(
      state,
      'description'
    ),
    paymentTakenAtFrontDesk: addRoomCheckFormSelector(
      state,
      'paymentTakenAtFrontDesk'
    ),
    imageIdentifiers: addRoomCheckFormSelector(state, 'imageIdentifiers'),
    inventoryIdentifier: addRoomCheckFormSelector(state, 'inventoryIdentifier'),
    maintenanceIdentifier: addRoomCheckFormSelector(
      state,
      'maintenanceIdentifier'
    )
  },
  chargeForm: {
    oneOffChargeReason: chargeFormSelector(state, 'oneOffChargeReason'),
    description: chargeFormSelector(state, 'description'),
    paymentMethod: chargeFormSelector(state, 'paymentMethod'),
    paymentDate: chargeFormSelector(state, 'paymentDate'),
    createPayment: chargeFormSelector(state, 'createPayment'),
    amount: chargeFormSelector(state, 'amount')
  }
});

const mapDispatchToProps = {
  submit,
  fetchImageUrlAndFileName,
  fetchDocumentFileName
};

export default withModal(
  MODALS.CONFIRM_ADD_DEPARTURE_ROOM_CHECK,
  mapStateToModalData,
  mapDispatchToProps
)(ConfirmAddDepartureRoomCheckModal);
